import React, { useEffect, useState } from 'react';

/* External */
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

/* Material UI */
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import { Alert, Autocomplete } from '@material-ui/lab';
import { Typography } from '@material-ui/core';

/* Project */
import ButtonWithLoader from 'commons/containers/ButtonWithLoader';
import { MODULES_ACTIONS } from 'screens/Modules';

function ModuleForm(props) {
  const { isOpen, closeForm, categories, classes } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [moduleName, setModuleName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [error, setError] = useState('');

  const createModuleLoading = useSelector((state) => state.modules.createModuleLoading);
  const updateModuleLoading = useSelector((state) => state.modules.updateModuleLoading);
  const moduleToEdit = useSelector((state) => state.modules.moduleToEdit);

  useEffect(() => {
    if (moduleToEdit) {
      setModuleName(moduleToEdit.name);
      setSelectedCategory(categories.find((category) => category.id === moduleToEdit.categoryId));
    }
  }, [moduleToEdit, categories, isOpen]);

  const resetForm = () => {
    setModuleName('');
    setError('');
    setSelectedCategory(null);
  };

  const handleSubmit = () => {
    if (!moduleName?.trim() || !selectedCategory) {
      setError(t('modules.errors.emptyFields'));
      return;
    }

    setError(null);

    const payload = {
      name: moduleName.trim(),
      categoryId: selectedCategory?.id,
    };

    dispatch({
      type: moduleToEdit ? MODULES_ACTIONS.UPDATE_MODULE : MODULES_ACTIONS.CREATE_MODULE,
      payload: {
        module: { ...payload, id: moduleToEdit?.id || null },
        onSuccess: () => {
          closeForm();
          resetForm();
        },
        onError: () => {
          setError(t('modules.errors.apiError'));
        },
      },
    });
  };

  const closeModal = () => {
    closeForm();
    resetForm();
  };

  return (
    <Dialog fullWidth onClose={closeModal} aria-labelledby="create-edit-module" open={isOpen}>
      <DialogTitle>
        {moduleToEdit ? t('modules.updateModule') : t('modules.createModule')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} className={classes.container} alignItems="center">
          <Grid item xs={5}>
            <TextField
              inputProps={{ maxLength: 255 }}
              fullWidth
              id="standard-basic"
              label={t('modules.filters.name')}
              value={moduleName}
              name="modalModuleName"
              onChange={(e) => setModuleName(e.target.value)}
            />
          </Grid>
          <Grid item xs={5}>
            <Autocomplete
              id="categories-selector"
              options={categories}
              getOptionLabel={(option) => option.name}
              style={{ width: 300 }}
              value={selectedCategory}
              renderInput={(params) => (
                <TextField {...params} label={t('modules.filters.category')} variant="standard" />
              )}
              onChange={(event, newValue) => {
                setSelectedCategory(newValue);
              }}
            />
          </Grid>
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">
                <Typography>{error}</Typography>
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal} color="primary">
          {t('Cancel')}
        </Button>
        <ButtonWithLoader
          onClick={handleSubmit}
          buttonId="submitModule"
          loading={createModuleLoading || updateModuleLoading}>
          {moduleToEdit ? t('modules.updateModule') : t('modules.createModule')}
        </ButtonWithLoader>
      </DialogActions>
    </Dialog>
  );
}

export default ModuleForm;
